// src/modules/app-views/companies/pages/Init/components/tableColumns/fullNameWithAvatarColumn.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tooltip } from 'antd';
import { parseCnpjToDisplay, parseCpfToDisplay, parseNifToDisplay, parseNipcToDisplay, returnOnlyNumbers } from 'utils/helpers';
import translateX from 'utils/translateX';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getFlagUrl(cc) {
  if (!cc) return null;
  const code = String(cc).toLowerCase();
  if (code.length !== 2) return null;
  const mapped = code === 'ac' ? 'gb' : code;
  return `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.0.0/flags/4x3/${mapped}.svg`;
}

// *******************************************************************************
// COLUMN FUNCTION
// *******************************************************************************
function fullNameWithAvatarColumn(buildColumnProps, options = {}) {
  const {
    key = 'fullNameWithAvatar',
    title = 'company_name',
    dataIndex = 'name',
    width,
    columnProps = { sort: true, search: true },
    hideAvatar = false,
    subtitleField = 'hybrid', // 'cnpj', 'cpf', or 'hybrid' (auto-detect)
    avatarShape = 'square', // 'square' or 'circle'
  } = options;

  return {
    key,
    title: translateX(title),
    dataIndex,
    ...(width && { width }),
    ...buildColumnProps(dataIndex, columnProps),
    render: (_, r) => {
      // Build subtitle based on subtitleField option
      let subtitle = '-';
      if (subtitleField === 'cnpj' && r?.cnpj?.value) {
        subtitle = `CNPJ ${parseCnpjToDisplay(returnOnlyNumbers(r.cnpj.value))}`;
      } else if (subtitleField === 'cpf' && r?.cpf?.value) {
        subtitle = `CPF ${parseCpfToDisplay(returnOnlyNumbers(r.cpf.value))}`;
      } else if (subtitleField === 'hybrid') {
        if (r?.entityType === 'company' && r?.cnpj?.value) {
          subtitle = `CNPJ ${parseCnpjToDisplay(returnOnlyNumbers(r.cnpj.value))}`;
        } else if (r?.cpf?.value) {
          subtitle = `CPF ${parseCpfToDisplay(returnOnlyNumbers(r.cpf.value))}`;
        }
      }

      // Without avatar - simple layout
      if (hideAvatar) {
        const flagSrc = getFlagUrl(r?.countryCode);
        let displaySubtitle = subtitle;
        if (displaySubtitle === '-' && r?.entityType) {
          if (r?.entityType === 'company') {
            if (r?.cnpj?.value) {
              displaySubtitle = parseCnpjToDisplay(returnOnlyNumbers(r.cnpj.value));
            } else if (r?.nipc?.value) {
              displaySubtitle = `NIPC ${parseNipcToDisplay(returnOnlyNumbers(r.nipc.value))}`;
            }
          } else if (r?.entityType === 'person') {
            if (r?.cpf?.value) {
              displaySubtitle = parseCpfToDisplay(returnOnlyNumbers(r.cpf.value));
            } else if (r?.nif?.value) {
              displaySubtitle = `NIF ${parseNifToDisplay(returnOnlyNumbers(r.nif.value))}`;
            }
          }
        }
        return (
          <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {flagSrc && (
                <img
                  src={flagSrc}
                  alt={r?.countryCode}
                  style={{ width: 20, height: 14, flexShrink: 0, borderRadius: 2 }}
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <Tooltip title={r?.fullName}>
                <span className="font-weight-semibold text-truncate d-block">
                  {r?.fullName || '-'}
                </span>
              </Tooltip>
            </div>
            <span className="text-muted text-truncate d-block" style={{ fontSize: 12 }}>
              {displaySubtitle}
            </span>
          </div>
        );
      }

      // Avatar fallback chain: entity avatar → linked account avatar → initials
      const acct = r?.__accountLinked;
      const avatarSrc = r?.avatar?.base64Thumb || acct?.avatar?.base64Thumb;
      const avatarBackColor = r?.backColor || acct?.backColor;
      const avatarForeColor = r?.foreColor || acct?.foreColor;
      const flagSrc = getFlagUrl(r?.countryCode);

      // With avatar - full layout
      let displaySubtitleAvatar = subtitle;
      if (displaySubtitleAvatar === '-' && r?.entityType) {
        if (r?.entityType === 'company') {
          if (r?.cnpj?.value) {
            displaySubtitleAvatar = parseCnpjToDisplay(returnOnlyNumbers(r.cnpj.value));
          } else if (r?.nipc?.value) {
            displaySubtitleAvatar = returnOnlyNumbers(r.nipc.value);
          }
        } else if (r?.entityType === 'person') {
          if (r?.cpf?.value) {
            displaySubtitleAvatar = parseCpfToDisplay(returnOnlyNumbers(r.cpf.value));
          } else if (r?.nif?.value) {
            displaySubtitleAvatar = returnOnlyNumbers(r.nif.value);
          }
        }
      }
      return (
        <div className="avatar-with-border d-flex" style={{ gap: 12, minWidth: 0 }}>
          <SimpleAvatar
            src={avatarSrc}
            name={r?.fullName}
            size={48}
            shape={avatarShape}
            showShadow={false}
            backColor={avatarBackColor}
            foreColor={avatarForeColor}
          />
          <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {flagSrc && (
                <img
                  src={flagSrc}
                  alt={r?.countryCode}
                  style={{ width: 20, height: 14, flexShrink: 0, borderRadius: 2 }}
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <Tooltip title={r?.fullName}>
                <span className="font-weight-semibold text-truncate d-block">
                  {r?.fullName || '-'}
                </span>
              </Tooltip>
            </div>
            <span className="text-muted text-truncate d-block" style={{ fontSize: 12 }}>
              {displaySubtitleAvatar}
            </span>
          </div>
        </div>
      );
    },
  };
}

export default fullNameWithAvatarColumn;
